// ── Audio Unlock ──
// MOB-H5: iOS Safari (and other mobile browsers) start the AudioContext in a
// suspended state. The context cannot be resumed until a user gesture is
// processed. Until then, all music synthesis sites silently produce no sound,
// so the first round (and the menu music) play as silence.
//
// AudioUnlock installs a one-shot listener for the first user gesture
// (pointerdown / touchstart / keydown) and, on fire, resumes:
//   1. Howler's shared AudioContext (if it exists),
//   2. the MusicSystem AudioContext,
//   3. an optional caller-supplied callback (e.g. start menu music).
//
// It also offers a small "TAP TO ENABLE AUDIO" overlay for cases where the
// user is staring at a quiet screen and doesn't realize they need to tap.
//
// Safe in test environments — every environment-dependent access (Howler.ctx,
// document, AudioContext on the music system) is wrapped in try/catch and
// guarded with feature checks.

import { Howler } from 'howler';
import { musicSystem } from './music';
import { ambientAudio } from './ambient';

const OVERLAY_ID = 'audio-unlock-overlay';
const GESTURE_EVENTS: Array<'pointerdown' | 'touchstart' | 'keydown'> = [
  'pointerdown',
  'touchstart',
  'keydown',
];

export class AudioUnlock {
  private unlocked = false;
  private listener: ((e: Event) => void) | null = null;
  private overlayEl: HTMLDivElement | null = null;
  private onUnlocked: (() => void) | null = null;

  /**
   * Install a one-shot gesture listener. Call from app start (e.g. in main.ts
   * or game.ts after init). Safe to call before any UI renders.
   *
   * @param onUnlocked optional callback fired on the first gesture, after the
   *   audio contexts are resumed. Useful for kicking off menu music.
   */
  init(onUnlocked?: () => void): void {
    if (this.listener || this.unlocked) return;
    this.onUnlocked = onUnlocked ?? null;

    this.listener = (_e: Event) => {
      this.handleGesture();
    };

    if (typeof document === 'undefined') return;
    for (const evt of GESTURE_EVENTS) {
      try {
        // A-M-004: { once: true } lets the browser auto-detach after the
        // first event so any second listener invocation cannot slip through
        // (e.g. touchstart + pointerdown in the same task). handleGesture()
        // still calls removeListener() defensively for the cross-event
        // siblings that didn't fire.
        document.addEventListener(evt, this.listener, { once: true, passive: true } as any);
      } catch {
        // Older test stubs may not accept options object.
        try { document.addEventListener(evt, this.listener); } catch { /* unavailable */ }
      }
    }
  }

  /**
   * Render a subtle full-screen overlay prompting the user to tap. Idempotent.
   * Auto-hides on the first gesture (driven by handleGesture).
   */
  showOverlay(): void {
    if (typeof document === 'undefined') return;
    // If overlay already in DOM (either via this instance or a stale one),
    // do nothing — keep the call idempotent.
    const existing = (document as any).getElementById?.(OVERLAY_ID) ?? null;
    if (existing) {
      this.overlayEl = existing as HTMLDivElement;
      return;
    }

    let el: HTMLDivElement;
    try {
      el = document.createElement('div') as HTMLDivElement;
    } catch {
      return;
    }
    el.id = OVERLAY_ID;
    el.textContent = 'TAP TO ENABLE AUDIO';

    Object.assign(el.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0, 0, 0, 0.55)',
      color: 'rgba(255, 255, 255, 0.9)',
      fontFamily: 'sans-serif',
      fontSize: '18px',
      fontWeight: 'bold',
      letterSpacing: '0.1em',
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.8)',
      pointerEvents: 'none',
      userSelect: 'none',
      zIndex: '1500',
    } satisfies Partial<CSSStyleDeclaration>);

    try {
      document.body.appendChild(el);
    } catch {
      return;
    }
    this.overlayEl = el;
  }

  /** Remove the overlay if it exists. Safe to call multiple times. */
  hideOverlay(): void {
    if (this.overlayEl) {
      try { this.overlayEl.remove(); } catch { /* already detached */ }
      this.overlayEl = null;
    } else if (typeof document !== 'undefined') {
      // Defensive — if a foreign overlay with the same id exists, remove it too.
      const existing = (document as any).getElementById?.(OVERLAY_ID) ?? null;
      if (existing) {
        try { existing.remove(); } catch { /* no-op */ }
      }
    }
  }

  /** Tear down listeners and remove the overlay. */
  dispose(): void {
    this.removeListener();
    this.hideOverlay();
    this.onUnlocked = null;
    this.unlocked = false;
  }

  // ── Internal ────────────────────────────────────────────────────────────

  private handleGesture(): void {
    if (this.unlocked) return;
    this.unlocked = true;

    // Resume Howler's shared AudioContext if present (Howler lazy-creates it).
    try {
      const ctx = (Howler as any)?.ctx as { state?: string; resume?: () => Promise<void> } | undefined;
      if (ctx && ctx.state === 'suspended' && typeof ctx.resume === 'function') {
        ctx.resume().catch(() => { /* ignore — gesture should satisfy the policy */ });
      }
    } catch { /* Howler unavailable in test env */ }

    // Resume the MusicSystem AudioContext.
    try {
      const r = (musicSystem as any)?.resume?.();
      if (r && typeof r.catch === 'function') r.catch(() => { /* ignore */ });
    } catch { /* music system unavailable */ }

    // Resume the AmbientAudioSystem AudioContext. Mobile Safari leaves it
    // suspended otherwise — ambient layers (wind, hum, birds, drips) stay
    // silent until something else nudges the context. A-H-003.
    try {
      const r = (ambientAudio as any)?.resume?.();
      if (r && typeof r.catch === 'function') r.catch(() => { /* ignore */ });
    } catch { /* ambient audio unavailable */ }

    // Run caller-supplied callback (e.g. start menu music).
    if (this.onUnlocked) {
      try { this.onUnlocked(); } catch { /* ignore */ }
    }

    // Auto-hide overlay and detach listeners.
    this.hideOverlay();
    this.removeListener();
  }

  private removeListener(): void {
    if (!this.listener) return;
    if (typeof document !== 'undefined') {
      for (const evt of GESTURE_EVENTS) {
        try { document.removeEventListener(evt, this.listener); } catch { /* no-op */ }
      }
    }
    this.listener = null;
  }
}
